import { useState, useEffect, useRef } from "react";
import { auth, provider, db, storage, messaging, getToken, onMessage } from "./firebase";
import { signInWithPopup, signOut, onAuthStateChanged, signInWithEmailAndPassword, sendPasswordResetEmail, createUserWithEmailAndPassword } from "firebase/auth";
import {
  collection, addDoc, updateDoc, doc, onSnapshot,
  query, orderBy, serverTimestamp, getDoc, setDoc, deleteDoc, where
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";

// ─── CONSTANTS ───────────────────────────────────────────────────────────────
const DEFAULT_PROPERTIES = ["Hotell", "Stugby", "Sportcenter", "Brasserie Kerstin", "Knallen"];
const DEFAULT_DEPARTMENTS = ["Rum & Städ", "Teknik & VVS", "El", "Kök", "Utemiljö", "IT"];
const PRIORITIES = ["Låg", "Normal", "Hög", "Akut"];
const STATUSES = ["Ny", "Pågående", "Väntar material", "Klar", "Avbruten"];
const VAPID_KEY = "BHiMLNT7kJ8l7N2nJSb28Y_RSI2UzR8eD5bSRmWAfbWHUUxUFC9A2Nvwfngr639Ck21TnVFoIup4uYAKvMBD3qk";
const ALLOWED_DOMAINS = ["bruksvallsliden.se", "brasseriekerstin.se", "stigmyhrs.se"];

function isAllowedEmail(email) {
  if (!email) return false;
  const domain = email.split("@")[1]?.toLowerCase();
  return ALLOWED_DOMAINS.includes(domain);
}

const PRIORITY_META = {
  Låg:    { color: "#6b7280", bg: "#f3f4f6", dot: "#9ca3af" },
  Normal: { color: "#2563eb", bg: "#eff6ff", dot: "#3b82f6" },
  Hög:    { color: "#d97706", bg: "#fffbeb", dot: "#f59e0b" },
  Akut:   { color: "#dc2626", bg: "#fef2f2", dot: "#ef4444" },
};
const STATUS_META = {
  Ny:               { color: "#6366f1", bg: "#eef2ff" },
  Pågående:         { color: "#0891b2", bg: "#ecfeff" },
  "Väntar material":{ color: "#b45309", bg: "#fffbeb" },
  Klar:             { color: "#16a34a", bg: "#f0fdf4" },
  Avbruten:         { color: "#6b7280", bg: "#f9fafb" },
};

function fmtDate(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("sv-SE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
function fmtDateShort(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString("sv-SE", { day: "numeric", month: "short", year: "numeric" });
}
function initials(name) {
  if (!name) return "?";
  return name.split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
}

// ─── PUSH NOTIFICATIONS ───────────────────────────────────────────────────────
async function requestPushPermission(uid) {
  if (!messaging) return;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return;
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (token) {
      await updateDoc(doc(db, "users", uid), { fcmToken: token });
    }
  } catch (e) {
    console.log("Push permission error:", e);
  }
}

// ─── SMALL COMPONENTS ────────────────────────────────────────────────────────
function Avatar({ name, photo, size = 32 }) {
  const colors = ["#7c3aed","#0891b2","#059669","#d97706","#dc2626","#db2777"];
  const color = colors[(name || "?").charCodeAt(0) % colors.length];
  if (photo) return <img src={photo} alt={name} style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />;
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: color, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.35, fontWeight: 700, flexShrink: 0 }}>
      {initials(name)}
    </div>
  );
}
function Badge({ label, meta }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: meta.bg, color: meta.color }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: meta.dot || meta.color }} />{label}
    </span>
  );
}
function Modal({ title, onClose, children }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: "#fff", borderRadius: "20px 20px 0 0", width: "100%", maxWidth: 640, maxHeight: "92vh", overflowY: "auto", boxShadow: "0 -8px 40px rgba(0,0,0,0.15)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 20px 0" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#111827" }}>{title}</h2>
          <button onClick={onClose} style={{ background: "#f3f4f6", border: "none", borderRadius: "50%", width: 32, height: 32, cursor: "pointer", fontSize: 18, color: "#6b7280" }}>×</button>
        </div>
        <div style={{ padding: 20 }}>{children}</div>
      </div>
    </div>
  );
}
function FieldGroup({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>{label}</label>
      {children}
    </div>
  );
}
const inputStyle = { width: "100%", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "9px 12px", fontSize: 14, color: "#111827", background: "#fff", outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
function Btn({ children, onClick, variant = "primary", style, disabled }) {
  const styles = {
    primary:   { background: "#6366f1", color: "#fff", border: "none" },
    success:   { background: "#16a34a", color: "#fff", border: "none" },
    secondary: { background: "#f3f4f6", color: "#374151", border: "none" },
    danger:    { background: "#fef2f2", color: "#dc2626", border: "1.5px solid #fecaca" },
    ghost:     { background: "transparent", color: "#6366f1", border: "1.5px solid #e0e7ff" },
  };
  return (
    <button onClick={onClick} disabled={disabled}
      style={{ ...styles[variant], borderRadius: 10, padding: "9px 16px", fontSize: 14, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.6 : 1, fontFamily: "inherit", ...style }}
      onMouseDown={e => { if (!disabled) e.currentTarget.style.transform = "scale(0.97)"; }}
      onMouseUp={e => { e.currentTarget.style.transform = "scale(1)"; }}>
      {children}
    </button>
  );
}

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin, loading, settings }) {
  const [mode, setMode] = useState("google"); // "google" | "email"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  async function handleEmailLogin() {
    if (!email || !password) return;
    setEmailLoading(true);
    setError("");
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (e) {
      if (e.code === "auth/invalid-credential" || e.code === "auth/wrong-password" || e.code === "auth/user-not-found") {
        setError("Fel e-post eller lösenord.");
      } else {
        setError("Inloggningsfel. Försök igen.");
      }
    }
    setEmailLoading(false);
  }

  async function handleReset() {
    if (!email) { setError("Ange din e-postadress ovan."); return; }
    try {
      await sendPasswordResetEmail(auth, email);
      setResetSent(true);
      setError("");
    } catch(e) { setError("Kunde inte skicka återställningsmail."); }
  }

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #eef2ff 0%, #f5f5fb 50%, #ecfeff 100%)", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 24, padding: "40px 32px", maxWidth: 400, width: "100%", textAlign: "center", boxShadow: "0 8px 40px rgba(99,102,241,0.12)" }}>
        {settings?.logoUrl
          ? <img src={settings.logoUrl} alt="logo" style={{ height: 56, objectFit: "contain", margin: "0 auto 20px", display: "block" }} />
          : <div style={{ width: 56, height: 56, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, margin: "0 auto 20px" }}>🔧</div>}
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#111827", marginBottom: 4 }}>Serviceärenden</h1>
        <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 24 }}>{settings?.companyName || "Hotell Bruksvallsliden"}</p>

        {/* Tab switcher */}
        <div style={{ display: "flex", background: "#f3f4f6", borderRadius: 10, padding: 3, marginBottom: 24 }}>
          <button onClick={() => { setMode("google"); setError(""); }} style={{ flex: 1, padding: "7px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, background: mode === "google" ? "#fff" : "transparent", color: mode === "google" ? "#111827" : "#6b7280", boxShadow: mode === "google" ? "0 1px 4px rgba(0,0,0,0.1)" : "none", fontFamily: "inherit" }}>
            Google-konto
          </button>
          <button onClick={() => { setMode("email"); setError(""); }} style={{ flex: 1, padding: "7px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, background: mode === "email" ? "#fff" : "transparent", color: mode === "email" ? "#111827" : "#6b7280", boxShadow: mode === "email" ? "0 1px 4px rgba(0,0,0,0.1)" : "none", fontFamily: "inherit" }}>
            E-post & lösenord
          </button>
        </div>

        {mode === "google" && (
          <>
            <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>
              För @bruksvallsliden.se, @brasseriekerstin.se och @stigmyhrs.se
            </p>
            <button onClick={onLogin} disabled={loading}
              style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "13px 20px", border: "1.5px solid #e5e7eb", borderRadius: 12, background: "#fff", cursor: loading ? "wait" : "pointer", fontSize: 15, fontWeight: 600, color: "#374151", fontFamily: "inherit" }}>
              <svg width="20" height="20" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              {loading ? "Loggar in…" : "Logga in med Google"}
            </button>
          </>
        )}

        {mode === "email" && (
          <>
            <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 16 }}>För externa användare med inbjudan</p>
            {resetSent ? (
              <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
                <p style={{ margin: 0, fontSize: 13, color: "#16a34a" }}>✓ Återställningsmail skickat till {email}</p>
              </div>
            ) : (
              <>
                <input value={email} onChange={e => setEmail(e.target.value)} placeholder="E-postadress"
                  type="email" onKeyDown={e => { if (e.key === "Enter") handleEmailLogin(); }}
                  style={{ ...inputStyle, marginBottom: 10, textAlign: "left" }} />
                <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Lösenord"
                  type="password" onKeyDown={e => { if (e.key === "Enter") handleEmailLogin(); }}
                  style={{ ...inputStyle, marginBottom: 16, textAlign: "left" }} />
                {error && <p style={{ fontSize: 13, color: "#dc2626", marginBottom: 12, textAlign: "left" }}>{error}</p>}
                <button onClick={handleEmailLogin} disabled={emailLoading || !email || !password}
                  style={{ width: "100%", padding: "13px", background: "#6366f1", color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: emailLoading ? "wait" : "pointer", fontFamily: "inherit", opacity: (!email || !password) ? 0.6 : 1, marginBottom: 12 }}>
                  {emailLoading ? "Loggar in…" : "Logga in"}
                </button>
                <button onClick={handleReset} style={{ background: "none", border: "none", color: "#6366f1", fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                  Glömt lösenordet?
                </button>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ─── FILE UPLOADER ────────────────────────────────────────────────────────────
function FileUploader({ files = [], onAdd, onRemove, ticketId, showCamera = true }) {
  const imageRef = useRef();
  const cameraRef = useRef();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const IMAGE_TYPES = ["image/jpeg","image/png","image/gif","image/webp","image/heic"];

  async function handleUpload(file) {
    if (!file) return;
    setUploading(true);
    try {
      const storageRef = ref(storage, `tickets/${ticketId || "new"}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      onAdd({ url, name: file.name, type: file.type, size: file.size });
    } catch (err) {
      alert("Kunde inte ladda upp filen. Kontrollera att Firebase Storage är aktiverat.");
    }
    setUploading(false);
  }

  function isImage(f) { return f && IMAGE_TYPES.includes(f.type); }
  function fileIcon(f) {
    if (!f?.type) return "📎";
    if (f.type.includes("pdf")) return "📄";
    if (f.type.includes("word") || f.type.includes("document")) return "📝";
    if (f.type.includes("excel") || f.type.includes("sheet")) return "📊";
    if (f.type.includes("image")) return "🖼";
    return "📎";
  }
  function fmtSize(bytes) {
    if (!bytes) return "";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024*1024) return (bytes/1024).toFixed(0) + " KB";
    return (bytes/1024/1024).toFixed(1) + " MB";
  }

  return (
    <div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        {(files || []).map((f, i) => (
          <div key={i} style={{ position: "relative" }}>
            {isImage(f) ? (
              <div style={{ position: "relative", width: 72, height: 72 }}>
                <a href={f.url} target="_blank" rel="noreferrer">
                  <img src={f.url} alt={f.name} style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 10, border: "1.5px solid #e5e7eb", display: "block" }} />
                </a>
                {onRemove && confirmDelete !== i && (
                  <button onClick={() => setConfirmDelete(i)} style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: "#ef4444", border: "2px solid #fff", color: "#fff", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>×</button>
                )}
                {onRemove && confirmDelete === i && (
                  <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.7)", borderRadius: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4 }}>
                    <span style={{ fontSize: 10, color: "#fff" }}>Ta bort?</span>
                    <div style={{ display: "flex", gap: 4 }}>
                      <button onClick={() => { onRemove(i); setConfirmDelete(null); }} style={{ background: "#ef4444", border: "none", borderRadius: 4, color: "#fff", fontSize: 10, padding: "2px 6px", cursor: "pointer" }}>Ja</button>
                      <button onClick={() => setConfirmDelete(null)} style={{ background: "#6b7280", border: "none", borderRadius: 4, color: "#fff", fontSize: 10, padding: "2px 6px", cursor: "pointer" }}>Nej</button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 6, background: "#f9fafb", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "6px 10px", maxWidth: 200 }}>
                <span style={{ fontSize: 20 }}>{fileIcon(f)}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <a href={f.url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#374151", fontWeight: 600, textDecoration: "none", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</a>
                  <span style={{ fontSize: 10, color: "#9ca3af" }}>{fmtSize(f.size)}</span>
                </div>
                {onRemove && (
                  <button onClick={() => onRemove(i)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 16, padding: 0, flexShrink: 0 }}>×</button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {showCamera && (
          <button onClick={() => cameraRef.current?.click()} disabled={uploading}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 10, border: "1.5px dashed #d1d5db", background: "#fff", cursor: uploading ? "wait" : "pointer", fontSize: 12, color: "#6b7280", fontFamily: "inherit" }}>
            <span>📷</span> Kamera
          </button>
        )}
        <button onClick={() => imageRef.current?.click()} disabled={uploading}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 10, border: "1.5px dashed #d1d5db", background: "#fff", cursor: uploading ? "wait" : "pointer", fontSize: 12, color: "#6b7280", fontFamily: "inherit" }}>
          <span>🖼</span> Bild
        </button>
        <button onClick={() => fileRef.current?.click()} disabled={uploading}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 12px", borderRadius: 10, border: "1.5px dashed #d1d5db", background: "#fff", cursor: uploading ? "wait" : "pointer", fontSize: 12, color: "#6b7280", fontFamily: "inherit" }}>
          <span>📎</span> {uploading ? "Laddar…" : "Fil/dok"}
        </button>
        <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={e => handleUpload(e.target.files[0])} style={{ display: "none" }} />
        <input ref={imageRef} type="file" accept="image/*" onChange={e => handleUpload(e.target.files[0])} style={{ display: "none" }} />
        <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,image/*" onChange={e => handleUpload(e.target.files[0])} style={{ display: "none" }} />
      </div>
    </div>
  );
}

// ─── WORK LOG ─────────────────────────────────────────────────────────────────
function WorkLogPanel({ ticket, currentUser }) {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState("");
  const [address, setAddress] = useState(ticket.workDetails?.address || "");
  const [owner, setOwner] = useState(ticket.workDetails?.owner || "");
  const [saving, setSaving] = useState(false);
  const [entries, setEntries] = useState(ticket.workLog || []);

  useEffect(() => { setEntries(ticket.workLog || []); setAddress(ticket.workDetails?.address || ""); setOwner(ticket.workDetails?.owner || ""); }, [ticket]);

  async function saveDetails() {
    setSaving(true);
    await updateDoc(doc(db, "tickets", ticket.id), { workDetails: { address, owner }, updatedAt: serverTimestamp() });
    setSaving(false);
  }
  async function addEntry() {
    if (!hours || isNaN(parseFloat(hours))) return;
    setSaving(true);
    const entry = { id: Date.now().toString(), authorId: currentUser.uid, authorName: currentUser.displayName, hours: parseFloat(hours), date: new Date().toISOString().slice(0, 10), ts: new Date().toISOString() };
    const newLog = [...entries, entry];
    await updateDoc(doc(db, "tickets", ticket.id), { workLog: newLog, updatedAt: serverTimestamp() });
    setEntries(newLog);
    setHours("");
    setSaving(false);
  }
  const totalHours = entries.reduce((s, e) => s + (e.hours || 0), 0);
  return (
    <div style={{ marginBottom: 16 }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: "#f5f3ff", border: "1.5px solid #e0e7ff", borderRadius: 12, cursor: "pointer", fontFamily: "inherit" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, fontWeight: 700, color: "#6366f1" }}>
          🕐 Arbetstid & objektinfo
          {totalHours > 0 && <span style={{ background: "#6366f1", color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: 11 }}>{totalHours}h</span>}
        </span>
        <span style={{ color: "#6366f1", fontSize: 16, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.2s" }}>›</span>
      </button>
      {open && (
        <div style={{ border: "1.5px solid #e0e7ff", borderTop: "none", borderRadius: "0 0 12px 12px", padding: 14, background: "#fafafe" }}>
          <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Objektinformation</p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
            <div><label style={{ fontSize: 11, color: "#9ca3af", display: "block", marginBottom: 3 }}>Adress / Stuga</label><input value={address} onChange={e => setAddress(e.target.value)} placeholder="T.ex. Stuga 12B" style={{ ...inputStyle, fontSize: 13 }} /></div>
            <div><label style={{ fontSize: 11, color: "#9ca3af", display: "block", marginBottom: 3 }}>Ägare / Gäst</label><input value={owner} onChange={e => setOwner(e.target.value)} placeholder="T.ex. Svensson" style={{ ...inputStyle, fontSize: 13 }} /></div>
          </div>
          <Btn onClick={saveDetails} disabled={saving} variant="ghost" style={{ fontSize: 12, padding: "6px 12px", marginBottom: 16 }}>{saving ? "Sparar…" : "Spara objektinfo"}</Btn>
          <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Tidrapportering</p>
          {entries.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              {entries.map(e => (
                <div key={e.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #f0f0f8" }}>
                  <Avatar name={e.authorName} size={22} />
                  <span style={{ fontSize: 13, color: "#374151", flex: 1 }}>{e.authorName}</span>
                  <span style={{ fontSize: 12, color: "#9ca3af" }}>{e.date}</span>
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#6366f1", minWidth: 40, textAlign: "right" }}>{e.hours}h</span>
                </div>
              ))}
              <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Totalt: {totalHours}h</span>
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <input value={hours} onChange={e => setHours(e.target.value)} placeholder="Antal timmar, t.ex. 1.5" type="number" step="0.5" min="0" style={{ ...inputStyle, flex: 1, fontSize: 13 }} />
            <Btn onClick={addEntry} disabled={saving || !hours} style={{ padding: "9px 14px", fontSize: 13, whiteSpace: "nowrap" }}>+ Logga tid</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── ACTION BUTTONS ──────────────────────────────────────────────────────────
function StartButton({ ticket }) {
  if (ticket.status !== "Ny") return null;
  async function markStarted() {
    await updateDoc(doc(db, "tickets", ticket.id), { status: "Pågående", updatedAt: serverTimestamp() });
  }
  return (
    <button onClick={markStarted}
      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px", background: "#eff6ff", border: "2px solid #93c5fd", borderRadius: 12, cursor: "pointer", fontSize: 14, fontWeight: 700, color: "#2563eb", marginBottom: 16, fontFamily: "inherit" }}
      onMouseEnter={e => e.currentTarget.style.background = "#dbeafe"}
      onMouseLeave={e => e.currentTarget.style.background = "#eff6ff"}>
      ▶ Påbörja
    </button>
  );
}

function CompleteButton({ ticket }) {
  const [confirming, setConfirming] = useState(false);
  if (ticket.status === "Klar" || ticket.status === "Avbruten") return null;
  async function markDone() {
    await updateDoc(doc(db, "tickets", ticket.id), { status: "Klar", completedAt: serverTimestamp(), updatedAt: serverTimestamp() });
    setConfirming(false);
  }
  if (confirming) return (
    <div style={{ display: "flex", gap: 8, alignItems: "center", background: "#f0fdf4", borderRadius: 12, padding: "10px 14px", marginBottom: 16 }}>
      <span style={{ fontSize: 13, color: "#166534", flex: 1 }}>Markera ärendet som klart?</span>
      <Btn onClick={markDone} variant="success" style={{ padding: "6px 14px", fontSize: 13 }}>✓ Ja, klart!</Btn>
      <Btn onClick={() => setConfirming(false)} variant="secondary" style={{ padding: "6px 12px", fontSize: 13 }}>Avbryt</Btn>
    </div>
  );
  return (
    <button onClick={() => setConfirming(true)}
      style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "12px", background: "#f0fdf4", border: "2px solid #86efac", borderRadius: 12, cursor: "pointer", fontSize: 14, fontWeight: 700, color: "#16a34a", marginBottom: 16, fontFamily: "inherit" }}
      onMouseEnter={e => e.currentTarget.style.background = "#dcfce7"}
      onMouseLeave={e => e.currentTarget.style.background = "#f0fdf4"}>
      ✓ Markera som klar
    </button>
  );
}

// ─── DELETE TICKET BUTTON ────────────────────────────────────────────────────
function DeleteTicketButton({ ticketId, ticketTitle, onDeleted }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    setDeleting(true);
    await deleteDoc(doc(db, "tickets", ticketId));
    setDeleting(false);
    onDeleted();
  }

  if (confirming) return (
    <div style={{ background: "#fef2f2", borderRadius: 12, padding: "12px 14px" }}>
      <p style={{ margin: "0 0 10px", fontSize: 13, color: "#991b1b", fontWeight: 600 }}>
        🗑️ Ta bort "{ticketTitle}"?
      </p>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: "#dc2626" }}>
        Detta går inte att ångra. Ärendet och alla kommentarer raderas permanent.
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <Btn onClick={handleDelete} disabled={deleting} variant="danger" style={{ flex: 1 }}>
          {deleting ? "Raderar…" : "Ja, ta bort permanent"}
        </Btn>
        <Btn onClick={() => setConfirming(false)} variant="secondary">Avbryt</Btn>
      </div>
    </div>
  );

  return (
    <button onClick={() => setConfirming(true)}
      style={{ background: "none", border: "none", color: "#dc2626", fontSize: 13, cursor: "pointer", fontFamily: "inherit", padding: "4px 0", display: "flex", alignItems: "center", gap: 6, opacity: 0.7 }}
      onMouseEnter={e => e.currentTarget.style.opacity = "1"}
      onMouseLeave={e => e.currentTarget.style.opacity = "0.7"}>
      🗑️ Ta bort ärende
    </button>
  );
}

// ─── TICKET DETAIL ────────────────────────────────────────────────────────────
function TicketDetail({ ticket, currentUser, userMap, onClose, properties, categories }) {
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [files, setFiles] = useState(ticket.files || ticket.images?.map(url => ({ url, name: "Bild", type: "image/jpeg" })) || []);
  const isAdminOrLead = ["admin", "admin_tekniker", "avdelningsansvarig"].includes(currentUser.role);
  const isDone = ticket.status === "Klar" || ticket.status === "Avbruten";

  useEffect(() => {
    setFiles(ticket.files || ticket.images?.map(url => ({ url, name: "Bild", type: "image/jpeg" })) || []);
  }, [ticket]);

  async function updateField(field, val) {
    await updateDoc(doc(db, "tickets", ticket.id), { [field]: val, updatedAt: serverTimestamp() });
  }
  async function addFile(fileObj) {
    const newFiles = [...files, fileObj];
    setFiles(newFiles);
    await updateDoc(doc(db, "tickets", ticket.id), { files: newFiles, updatedAt: serverTimestamp() });
  }
  async function removeFile(index) {
    const newFiles = files.filter((_, i) => i !== index);
    setFiles(newFiles);
    await updateDoc(doc(db, "tickets", ticket.id), { files: newFiles, updatedAt: serverTimestamp() });
  }
  async function addComment() {
    if (!comment.trim()) return;
    setSaving(true);
    const comments = [...(ticket.comments || []), { id: Date.now().toString(), authorId: currentUser.uid, authorName: currentUser.displayName, text: comment.trim(), ts: new Date().toISOString() }];
    await updateDoc(doc(db, "tickets", ticket.id), { comments, updatedAt: serverTimestamp() });
    setComment("");
    setSaving(false);
  }
  const assignee = userMap[ticket.assigneeId];

  return (
    <Modal title={`Ärende #${ticket.id.slice(-5).toUpperCase()}`} onClose={onClose}>
      <h3 style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 800, color: "#111827", lineHeight: 1.3 }}>{ticket.title}</h3>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        <Badge label={ticket.priority} meta={PRIORITY_META[ticket.priority]} />
        <Badge label={ticket.status} meta={STATUS_META[ticket.status]} />
        <span style={{ fontSize: 11, color: "#9ca3af" }}>{ticket.property} · {ticket.department}</span>
      </div>
      {ticket.status === "Klar" && (
        <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 12, padding: "10px 14px", marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18 }}>✅</span>
          <div>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#16a34a" }}>Avslutat ärende</p>
            {ticket.completedAt && <p style={{ margin: 0, fontSize: 11, color: "#4ade80" }}>Klart {fmtDate(ticket.completedAt)}</p>}
          </div>
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <StartButton ticket={ticket} />
        <CompleteButton ticket={ticket} />
      </div>
      {ticket.description && <div style={{ background: "#f9fafb", borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 14, color: "#374151", lineHeight: 1.6 }}>{ticket.description}</div>}
      {(ticket.workDetails?.address || ticket.workDetails?.owner) && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          {ticket.workDetails?.address && <div style={{ background: "#eff6ff", borderRadius: 8, padding: "6px 10px", fontSize: 12 }}><span style={{ color: "#9ca3af" }}>Adress: </span><span style={{ fontWeight: 600, color: "#1d4ed8" }}>{ticket.workDetails.address}</span></div>}
          {ticket.workDetails?.owner && <div style={{ background: "#eff6ff", borderRadius: 8, padding: "6px 10px", fontSize: 12 }}><span style={{ color: "#9ca3af" }}>Ägare: </span><span style={{ fontWeight: 600, color: "#1d4ed8" }}>{ticket.workDetails.owner}</span></div>}
        </div>
      )}
      <div style={{ marginBottom: 16 }}>
        <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>Bilder & filer</p>
        <FileUploader files={files} onAdd={addFile} onRemove={removeFile} ticketId={ticket.id} />
      </div>
      <WorkLogPanel ticket={ticket} currentUser={currentUser} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <div>
          <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase" }}>Rapporterad av</p>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}><Avatar name={ticket.reporterName} size={22} /><span style={{ fontSize: 13, color: "#374151" }}>{ticket.reporterName}</span></div>
        </div>
        <div>
          <p style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 600, color: "#9ca3af", textTransform: "uppercase" }}>Skapad</p>
          <span style={{ fontSize: 13, color: "#374151" }}>{fmtDate(ticket.createdAt)}</span>
        </div>
      </div>
      {isAdminOrLead && !isDone && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
          <FieldGroup label="Status"><select value={ticket.status} onChange={e => updateField("status", e.target.value)} style={inputStyle}>{STATUSES.map(s => <option key={s}>{s}</option>)}</select></FieldGroup>
          <FieldGroup label="Prioritet"><select value={ticket.priority} onChange={e => updateField("priority", e.target.value)} style={inputStyle}>{PRIORITIES.map(p => <option key={p}>{p}</option>)}</select></FieldGroup>
          <FieldGroup label="Tilldela">
            <select value={ticket.assigneeId || ""} onChange={e => updateField("assigneeId", e.target.value || null)} style={inputStyle}>
              <option value="">Ej tilldelad</option>
              {Object.values(userMap).map(u => <option key={u.uid} value={u.uid}>{u.displayName}</option>)}
            </select>
          </FieldGroup>
        </div>
      )}
      {assignee && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#f0fdf4", borderRadius: 10, padding: "8px 12px", marginBottom: 16 }}>
          <Avatar name={assignee.displayName} photo={assignee.photoURL} size={28} />
          <div><p style={{ margin: 0, fontSize: 12, color: "#16a34a", fontWeight: 600 }}>Tilldelad</p><p style={{ margin: 0, fontSize: 13, color: "#166534" }}>{assignee.displayName}</p></div>
        </div>
      )}
      <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 16 }}>
        <p style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 700, color: "#374151" }}>Kommentarer ({(ticket.comments || []).length})</p>
        {(ticket.comments || []).length === 0 && <p style={{ fontSize: 13, color: "#9ca3af", marginBottom: 12 }}>Inga kommentarer ännu.</p>}
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
          {(ticket.comments || []).map(c => (
            <div key={c.id} style={{ display: "flex", gap: 10 }}>
              <Avatar name={c.authorName} size={30} />
              <div style={{ background: "#f9fafb", borderRadius: "4px 12px 12px 12px", padding: "8px 12px", flex: 1 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: "#374151" }}>{c.authorName}</span>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>{fmtDate(c.ts)}</span>
                </div>
                <p style={{ margin: 0, fontSize: 13, color: "#374151", lineHeight: 1.5 }}>{c.text}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <input value={comment} onChange={e => setComment(e.target.value)} placeholder="Skriv en kommentar…"
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); addComment(); } }}
            style={{ ...inputStyle, flex: 1 }} />
          <Btn onClick={addComment} disabled={saving || !comment.trim()}>Skicka</Btn>
        </div>
      </div>

      {/* Delete ticket - admin only */}
      {isAdminOrLead && (
        <div style={{ borderTop: "1px solid #fee2e2", paddingTop: 16, marginTop: 4 }}>
          <DeleteTicketButton ticketId={ticket.id} ticketTitle={ticket.title} onDeleted={onClose} />
        </div>
      )}
    </Modal>
  );
}

// ─── NEW TICKET ───────────────────────────────────────────────────────────────
function NewTicketForm({ currentUser, userMap, onClose, properties, categories }) {
  const [form, setForm] = useState({ title: "", description: "", property: "", department: "", priority: "Normal", assigneeId: "" });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const isAdminOrLead = ["admin", "admin_tekniker", "avdelningsansvarig"].includes(currentUser.role);
  function set(f, v) { setForm(p => ({ ...p, [f]: v })); }
  async function save() {
    if (!form.title || !form.property || !form.department) return;
    setSaving(true);
    const tid = Date.now().toString();
    await addDoc(collection(db, "tickets"), { ...form, assigneeId: form.assigneeId || null, status: "Ny", reporterId: currentUser.uid, reporterName: currentUser.displayName, comments: [], files, workLog: [], workDetails: {}, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    setSaving(false);
    onClose();
  }
  return (
    <Modal title="Nytt ärende" onClose={onClose}>
      <FieldGroup label="Titel *"><input value={form.title} onChange={e => set("title", e.target.value)} placeholder="Beskriv problemet kort…" style={inputStyle} /></FieldGroup>
      <FieldGroup label="Beskrivning"><textarea value={form.description} onChange={e => set("description", e.target.value)} placeholder="Mer detaljer…" rows={3} style={{ ...inputStyle, resize: "vertical" }} /></FieldGroup>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FieldGroup label="Fastighet/avdelning *">
          <select value={form.property} onChange={e => set("property", e.target.value)} style={inputStyle}>
            <option value="">Välj…</option>
            {properties.map(p => <option key={p}>{p}</option>)}
          </select>
        </FieldGroup>
        <FieldGroup label="Kategori *">
          <select value={form.department} onChange={e => set("department", e.target.value)} style={inputStyle}>
            <option value="">Välj…</option>
            {categories.map(d => <option key={d}>{d}</option>)}
          </select>
        </FieldGroup>
        <FieldGroup label="Prioritet"><select value={form.priority} onChange={e => set("priority", e.target.value)} style={inputStyle}>{PRIORITIES.map(p => <option key={p}>{p}</option>)}</select></FieldGroup>
        {isAdminOrLead && (
          <FieldGroup label="Tilldela">
            <select value={form.assigneeId} onChange={e => set("assigneeId", e.target.value)} style={inputStyle}>
              <option value="">Ej tilldelad</option>
              {Object.values(userMap).map(u => <option key={u.uid} value={u.uid}>{u.displayName}</option>)}
            </select>
          </FieldGroup>
        )}
      </div>
      <FieldGroup label="Bilder & filer (valfritt)">
        <FileUploader files={files} onAdd={f => setFiles(p => [...p, f])} ticketId={"new_" + Date.now()} />
      </FieldGroup>
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <Btn onClick={save} disabled={saving || !form.title || !form.property || !form.department} style={{ flex: 1 }}>{saving ? "Sparar…" : "Skapa ärende"}</Btn>
        <Btn onClick={onClose} variant="secondary">Avbryt</Btn>
      </div>
    </Modal>
  );
}

// ─── TICKET CARD ──────────────────────────────────────────────────────────────
function TicketCard({ ticket, userMap, onClick }) {
  const assignee = userMap[ticket.assigneeId];
  const pm = PRIORITY_META[ticket.priority] || PRIORITY_META.Normal;
  const totalHours = (ticket.workLog || []).reduce((s, e) => s + (e.hours || 0), 0);
  const fileCount = (ticket.files || ticket.images || []).length;
  return (
    <div onClick={onClick} style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", cursor: "pointer", borderLeft: `4px solid ${pm.dot}`, boxShadow: ticket.priority === "Akut" ? `0 0 0 2px ${pm.dot}30, 0 2px 12px rgba(0,0,0,0.06)` : "0 1px 6px rgba(0,0,0,0.06)", marginBottom: 8, transition: "transform 0.12s" }}
      onMouseEnter={e => e.currentTarget.style.transform = "translateY(-1px)"}
      onMouseLeave={e => e.currentTarget.style.transform = "none"}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
        <p style={{ margin: "0 0 6px", fontSize: 14, fontWeight: 700, color: "#111827", flex: 1, lineHeight: 1.35 }}>{ticket.title}</p>
        {assignee ? <Avatar name={assignee.displayName} photo={assignee.photoURL} size={28} /> : <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#f3f4f6", border: "1.5px dashed #d1d5db", flexShrink: 0 }} />}
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <Badge label={ticket.priority} meta={pm} />
        <Badge label={ticket.status} meta={STATUS_META[ticket.status] || STATUS_META.Ny} />
        <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: "auto" }}>{ticket.property}</span>
      </div>
      <div style={{ display: "flex", gap: 10, marginTop: 6, fontSize: 11, color: "#9ca3af" }}>
        {fileCount > 0 && <span>🖼 {fileCount}</span>}
        {totalHours > 0 && <span>🕐 {totalHours}h</span>}
        {(ticket.comments || []).length > 0 && <span>💬 {ticket.comments.length}</span>}
        {ticket.workDetails?.address && <span>📍 {ticket.workDetails.address}</span>}
        <span style={{ marginLeft: "auto" }}>{fmtDate(ticket.updatedAt)}</span>
      </div>
    </div>
  );
}

// ─── WEATHER VIEW ─────────────────────────────────────────────────────────────
const NETATMO_CLIENT_ID = "6a234560afa010e409060a5c";
const NETATMO_CLIENT_SECRET = "CoO8gP4JoN0EH1G9iUbfH4w0YkVhJNcVVh6b8XVZwyf";
const NETATMO_REFRESH_TOKEN = "55992b2a1b775919b95e04fa|3ac8020fcb2193cc102c6adc0fc5d8e3";

function WeatherView() {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  async function getAccessToken() {
    const resp = await fetch("https://api.netatmo.com/oauth2/token", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: NETATMO_REFRESH_TOKEN,
        client_id: NETATMO_CLIENT_ID,
        client_secret: NETATMO_CLIENT_SECRET,
      }),
    });
    const data = await resp.json();
    return data.access_token;
  }

  async function fetchWeather() {
    setLoading(true);
    setError("");
    try {
      const token = await getAccessToken();
      const resp = await fetch("https://api.netatmo.com/api/getstationsdata", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await resp.json();
      if (data.body?.devices?.length > 0) {
        // Extract outdoor temp and wind from modules
        const device = data.body.devices[0];
        const outdoor = device.modules?.find(m => m.type === "NAModule1");
        const wind = device.modules?.find(m => m.type === "NAModule3");
        setWeather({ outdoor, wind, stationName: device.station_name });
        setLastUpdated(new Date());
      } else {
        setError("Inga stationer hittades.");
      }
    } catch (e) {
      setError("Kunde inte hämta väderdata.");
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchWeather();
    const interval = setInterval(fetchWeather, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  function tempColor(t) {
    if (t <= -15) return "#93c5fd";
    if (t <= -5) return "#60a5fa";
    if (t <= 0) return "#34d399";
    if (t <= 10) return "#fbbf24";
    return "#f87171";
  }

  function windColor(w) {
    if (w < 5) return "#16a34a";
    if (w < 15) return "#ca8a04";
    if (w < 25) return "#ea580c";
    return "#dc2626";
  }

  function windDesc(w) {
    if (w < 1) return "Stiltje";
    if (w < 5) return "Svag vind";
    if (w < 11) return "Lätt bris";
    if (w < 19) return "Måttlig bris";
    if (w < 28) return "Frisk bris";
    if (w < 38) return "Styv bris";
    return "Hård vind";
  }

  function windDir(deg) {
    const dirs = ["N","NNO","NO","ONO","O","OSO","SO","SSO","S","SSV","SV","VSV","V","VNV","NV","NNV"];
    return dirs[Math.round(deg / 22.5) % 16];
  }

  const temp = weather?.outdoor?.dashboard_data?.Temperature;
  const hum = weather?.outdoor?.dashboard_data?.Humidity;
  const windSpeed = weather?.wind?.dashboard_data?.WindStrength;
  const windGust = weather?.wind?.dashboard_data?.GustStrength;
  const windAngle = weather?.wind?.dashboard_data?.WindAngle;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#111827" }}>🏔️ Bruksvallarna</p>
          {lastUpdated && <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>Uppdaterad {lastUpdated.toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}</p>}
        </div>
        <button onClick={fetchWeather} disabled={loading}
          style={{ background: "#f3f4f6", border: "none", borderRadius: 8, padding: "7px 12px", fontSize: 13, cursor: loading ? "wait" : "pointer", fontFamily: "inherit", color: "#374151" }}>
          {loading ? "⏳" : "🔄 Uppdatera"}
        </button>
      </div>

      {loading && !weather && (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af" }}>
          <div style={{ fontSize: 48, marginBottom: 8 }}>🌡️</div>
          <p>Hämtar väderdata…</p>
        </div>
      )}

      {error && (
        <div style={{ background: "#fef2f2", border: "1.5px solid #fecaca", borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
          <p style={{ margin: 0, fontSize: 13, color: "#dc2626" }}>⚠️ {error}</p>
        </div>
      )}

      {weather && (
        <div>
          {/* Temperature card */}
          {temp !== undefined && (
            <div style={{ background: "#fff", borderRadius: 20, padding: 24, marginBottom: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
              <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em" }}>UTOMHUSTEMPERATUR</p>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
                <p style={{ margin: 0, fontSize: 72, fontWeight: 900, color: tempColor(temp), lineHeight: 1 }}>
                  {temp.toFixed(1)}°
                </p>
                <div style={{ paddingBottom: 8 }}>
                  {hum !== undefined && (
                    <p style={{ margin: 0, fontSize: 14, color: "#6366f1", fontWeight: 600 }}>💧 {hum}% luftfuktighet</p>
                  )}
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "#9ca3af" }}>
                    {temp <= 0 ? "❄️ Under nollan" : temp <= 10 ? "🌤️ Kyligt" : "☀️ Behagligt"}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Wind card */}
          {windSpeed !== undefined && (
            <div style={{ background: "#fff", borderRadius: 20, padding: 24, marginBottom: 12, boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
              <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.08em" }}>VIND</p>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
                <p style={{ margin: 0, fontSize: 72, fontWeight: 900, color: windColor(windSpeed), lineHeight: 1 }}>
                  {windSpeed}
                  <span style={{ fontSize: 20, fontWeight: 600 }}> km/h</span>
                </p>
                <div style={{ paddingBottom: 8 }}>
                  {windAngle !== undefined && (
                    <p style={{ margin: 0, fontSize: 14, color: "#374151", fontWeight: 600 }}>
                      🧭 {windDir(windAngle)} ({windAngle}°)
                    </p>
                  )}
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "#9ca3af" }}>{windDesc(windSpeed)}</p>
                  {windGust !== undefined && windGust > windSpeed && (
                    <p style={{ margin: "4px 0 0", fontSize: 13, color: "#ea580c", fontWeight: 600 }}>
                      💨 Byar upp till {windGust} km/h
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {!temp && !windSpeed && (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af" }}>
              <p>Inga moduler hittades. Kontrollera att utomhus- och vindmodul är anslutna.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── WISHLIST VIEW ────────────────────────────────────────────────────────────
function WishlistView({ currentUser }) {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const isAdminOrLead = ["admin", "admin_tekniker", "avdelningsansvarig"].includes(currentUser.role);

  useEffect(() => {
    let q;
    if (isAdminOrLead) {
      q = query(collection(db, "wishlist"), orderBy("createdAt", "desc"));
    } else {
      q = query(collection(db, "wishlist"), where("reporterId", "==", currentUser.uid), orderBy("createdAt", "desc"));
    }
    return onSnapshot(q, snap => setItems(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
  }, [currentUser.uid, isAdminOrLead]);

  async function addItem() {
    if (!title.trim()) return;
    setSaving(true);
    await addDoc(collection(db, "wishlist"), {
      title: title.trim(),
      description: description.trim(),
      files,
      reporterId: currentUser.uid,
      reporterName: currentUser.displayName,
      status: "Öppen",
      createdAt: serverTimestamp(),
    });
    setTitle("");
    setDescription("");
    setFiles([]);
    setShowForm(false);
    setSaving(false);
  }

  async function updateStatus(id, status) {
    await updateDoc(doc(db, "wishlist", id), { status });
  }

  async function deleteItem(id) {
    await deleteDoc(doc(db, "wishlist", id));
  }

  const statusColor = { "Öppen": "#6366f1", "Planerad": "#0891b2", "Klar": "#16a34a", "Avböjd": "#9ca3af" };
  const statusBg = { "Öppen": "#eef2ff", "Planerad": "#ecfeff", "Klar": "#f0fdf4", "Avböjd": "#f9fafb" };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#374151" }}>Önskelista</p>
          <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>Idéer & förbättringar att ta tag i</p>
        </div>
        <button onClick={() => setShowForm(s => !s)}
          style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: 10, padding: "8px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          {showForm ? "Avbryt" : "+ Lägg till"}
        </button>
      </div>

      {showForm && (
        <div style={{ background: "#fff", borderRadius: 14, padding: 16, marginBottom: 16, boxShadow: "0 1px 6px rgba(0,0,0,0.06)" }}>
          <FieldGroup label="Vad önskar du?">
            <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Beskriv önskemålet kort…" style={inputStyle} />
          </FieldGroup>
          <FieldGroup label="Mer detaljer (valfritt)">
            <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Bakgrund, varför det behövs…" rows={3} style={{ ...inputStyle, resize: "vertical" }} />
          </FieldGroup>
          <FieldGroup label="Bilder & filer (valfritt)">
            <FileUploader files={files} onAdd={f => setFiles(p => [...p, f])} ticketId={"wish_" + Date.now()} />
          </FieldGroup>
          <Btn onClick={addItem} disabled={saving || !title.trim()} style={{ width: "100%" }}>{saving ? "Sparar…" : "Skicka in önskemål"}</Btn>
        </div>
      )}

      {items.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af" }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>⭐</div>
          <p style={{ fontSize: 14 }}>{isAdminOrLead ? "Inga önskemål inkomna ännu." : "Du har inga önskemål ännu."}</p>
        </div>
      ) : (
        items.map(item => (
          <div key={item.id} style={{ background: "#fff", borderRadius: 14, padding: "14px 16px", marginBottom: 8, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: "#111827" }}>{item.title}</p>
                {item.description && <p style={{ margin: "0 0 6px", fontSize: 13, color: "#6b7280", lineHeight: 1.4 }}>{item.description}</p>}
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 20, background: statusBg[item.status] || "#f3f4f6", color: statusColor[item.status] || "#6b7280", fontWeight: 600 }}>{item.status}</span>
                  {isAdminOrLead && <span style={{ fontSize: 11, color: "#9ca3af" }}>av {item.reporterName}</span>}
                  <span style={{ fontSize: 11, color: "#d1d5db" }}>{fmtDateShort(item.createdAt)}</span>
                </div>
              </div>
              {isAdminOrLead && (
                <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
                  <select value={item.status} onChange={e => updateStatus(item.id, e.target.value)}
                    style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "4px 6px", fontSize: 11, color: "#374151", background: "#fff", fontFamily: "inherit", cursor: "pointer" }}>
                    {["Öppen","Planerad","Klar","Avböjd"].map(s => <option key={s}>{s}</option>)}
                  </select>
                  <button onClick={() => deleteItem(item.id)} style={{ background: "#fef2f2", border: "none", borderRadius: 8, padding: "4px 6px", fontSize: 11, color: "#dc2626", cursor: "pointer", fontFamily: "inherit" }}>Ta bort</button>
                </div>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

// ─── HISTORY VIEW ─────────────────────────────────────────────────────────────
function HistoryView({ tickets, userMap, onSelect, companyName, properties }) {
  const [search, setSearch] = useState("");
  const [filterProp, setFilterProp] = useState("Alla");
  const [exportMonth, setExportMonth] = useState("");
  const [exporting, setExporting] = useState(false);

  const done = tickets
    .filter(t => t.status === "Klar" || t.status === "Avbruten")
    .filter(t => filterProp === "Alla" || t.property === filterProp)
    .filter(t => !search || t.title?.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const da = (a.completedAt?.toDate?.() || new Date(0)).getTime();
      const db2 = (b.completedAt?.toDate?.() || new Date(0)).getTime();
      return db2 - da;
    });

  const months = [...new Set(tickets.filter(t => t.status === "Klar" || t.status === "Avbruten").map(t => {
    const d = t.completedAt?.toDate?.() || t.updatedAt?.toDate?.() || new Date();
    return d.toLocaleDateString("sv-SE", { year: "numeric", month: "2-digit" });
  }))].sort().reverse();

  const groups = {};
  done.forEach(t => {
    const d = t.completedAt?.toDate?.() || t.updatedAt?.toDate?.() || new Date();
    const key = d.toLocaleDateString("sv-SE", { month: "long", year: "numeric" });
    if (!groups[key]) groups[key] = [];
    groups[key].push(t);
  });

  function exportCSV() {
    if (!exportMonth) return;
    setExporting(true);
    const [year, month] = exportMonth.split("-");
    const filtered = tickets.filter(t => {
      if (t.status !== "Klar" && t.status !== "Avbruten") return false;
      const d = t.completedAt?.toDate?.() || t.updatedAt?.toDate?.() || new Date();
      return d.getFullYear() === parseInt(year) && (d.getMonth() + 1) === parseInt(month);
    });
    const rows = [["Ärende-ID","Titel","Fastighet/avdelning","Kategori","Prioritet","Status","Adress/Objekt","Ägare/Gäst","Rapporterad av","Tilldelad","Skapad","Avslutat","Totalt timmar","Tidlogg (detalj)","Kommentarer"]];
    filtered.forEach(t => {
      const assignee = userMap[t.assigneeId];
      const totalHours = (t.workLog || []).reduce((s, e) => s + (e.hours || 0), 0);
      const workLogDetail = (t.workLog || []).map(e => `${e.date} ${e.authorName} ${e.hours}h`).join(" | ");
      const comments = (t.comments || []).map(c => `${c.authorName}: ${c.text}`).join(" | ");
      rows.push([t.id.slice(-5).toUpperCase(), t.title, t.property, t.department, t.priority, t.status, t.workDetails?.address || "", t.workDetails?.owner || "", t.reporterName || "", assignee?.displayName || "", t.createdAt?.toDate ? t.createdAt.toDate().toLocaleDateString("sv-SE") : "", t.completedAt?.toDate ? t.completedAt.toDate().toLocaleDateString("sv-SE") : "", totalHours, workLogDetail, comments]);
    });
    const csv = rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${companyName || "Serviceärenden"}_${exportMonth}.csv`; a.click();
    URL.revokeObjectURL(url);
    setExporting(false);
  }

  return (
    <div>
      <div style={{ background: "#fff", borderRadius: 14, padding: 16, marginBottom: 16, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
        <p style={{ margin: "0 0 10px", fontSize: 13, fontWeight: 700, color: "#374151" }}>📊 Exportera ärendelogg</p>
        <p style={{ margin: "0 0 10px", fontSize: 12, color: "#9ca3af" }}>Exportera avslutade ärenden per månad till CSV för fakturering och interndebitering.</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <select value={exportMonth} onChange={e => setExportMonth(e.target.value)} style={{ ...inputStyle, flex: 1, minWidth: 160 }}>
            <option value="">Välj månad…</option>
            {months.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <Btn onClick={exportCSV} disabled={!exportMonth || exporting} style={{ whiteSpace: "nowrap" }}>{exporting ? "Exporterar…" : "⬇ Ladda ner CSV"}</Btn>
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Sök i historik…" style={{ ...inputStyle, flex: 1, minWidth: 140, borderRadius: 20 }} />
        <select value={filterProp} onChange={e => setFilterProp(e.target.value)} style={{ border: "1.5px solid #e5e7eb", borderRadius: 20, padding: "6px 10px", fontSize: 12, color: "#374151", background: "#fff", outline: "none", fontFamily: "inherit" }}>
          {["Alla", ...properties].map(p => <option key={p}>{p}</option>)}
        </select>
      </div>
      {done.length === 0 ? (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af" }}><div style={{ fontSize: 40, marginBottom: 8 }}>📋</div><p style={{ fontSize: 14 }}>Inga avslutade ärenden ännu.</p></div>
      ) : Object.entries(groups).map(([month, items]) => (
        <div key={month}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, marginTop: 16 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "capitalize" }}>{month}</span>
            <span style={{ fontSize: 11, color: "#d1d5db", background: "#f3f4f6", borderRadius: 20, padding: "1px 8px" }}>{items.length} st</span>
            <span style={{ fontSize: 11, color: "#9ca3af" }}>· {items.reduce((s,t) => s+(t.workLog||[]).reduce((ss,e)=>ss+e.hours,0), 0)}h totalt</span>
          </div>
          {items.map(t => (
            <div key={t.id} onClick={() => onSelect(t)} style={{ background: "#fff", borderRadius: 14, padding: "12px 16px", cursor: "pointer", borderLeft: "4px solid #d1d5db", boxShadow: "0 1px 4px rgba(0,0,0,0.04)", marginBottom: 8, opacity: 0.85, transition: "opacity 0.12s" }}
              onMouseEnter={e => e.currentTarget.style.opacity = "1"} onMouseLeave={e => e.currentTarget.style.opacity = "0.85"}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 600, color: "#374151" }}>{t.title}</p>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <Badge label={t.status} meta={STATUS_META[t.status] || STATUS_META.Klar} />
                    <span style={{ fontSize: 11, color: "#9ca3af" }}>{t.property} · {t.department}</span>
                    {t.workDetails?.address && <span style={{ fontSize: 11, color: "#6366f1" }}>📍 {t.workDetails.address}</span>}
                    {(t.workLog||[]).reduce((s,e)=>s+e.hours,0) > 0 && <span style={{ fontSize: 11, color: "#0891b2" }}>🕐 {(t.workLog||[]).reduce((s,e)=>s+e.hours,0)}h</span>}
                  </div>
                </div>
                <p style={{ margin: 0, fontSize: 10, color: "#9ca3af", flexShrink: 0, marginLeft: 8 }}>{fmtDateShort(t.completedAt || t.updatedAt)}</p>
              </div>
            </div>
          ))}
        </div>
      ))}
      <p style={{ textAlign: "center", fontSize: 11, color: "#d1d5db", marginTop: 16 }}>{done.length} avslutade ärenden totalt</p>
    </div>
  );
}

// ─── LIST EDITOR ──────────────────────────────────────────────────────────────
function InviteUser() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("extern");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  async function invite() {
    if (!email || !name || !password) return;
    setSaving(true);
    setError("");
    try {
      // Create the user in Firebase Auth
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      // Save profile in Firestore
      await setDoc(doc(db, "users", cred.user.uid), {
        uid: cred.user.uid,
        displayName: name,
        email: email,
        photoURL: "",
        role: role,
        invitedAt: serverTimestamp(),
      });
      // Send password reset so they can set their own password
      await sendPasswordResetEmail(auth, email);
      setDone(true);
      setEmail(""); setName(""); setPassword("");
    } catch (e) {
      if (e.code === "auth/email-already-in-use") setError("Den e-postadressen finns redan.");
      else if (e.code === "auth/weak-password") setError("Lösenordet måste vara minst 6 tecken.");
      else setError("Fel: " + e.message);
    }
    setSaving(false);
  }

  if (done) return (
    <div style={{ background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: 12, padding: "12px 16px" }}>
      <p style={{ margin: 0, fontSize: 13, color: "#16a34a", fontWeight: 600 }}>✓ Användare skapad!</p>
      <p style={{ margin: "4px 0 8px", fontSize: 12, color: "#166534" }}>Ett mail har skickats så de kan sätta sitt eget lösenord.</p>
      <button onClick={() => setDone(false)} style={{ background: "none", border: "none", color: "#6366f1", fontSize: 13, cursor: "pointer", fontFamily: "inherit", padding: 0 }}>+ Bjud in en till</button>
    </div>
  );

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
        <div>
          <label style={{ fontSize: 11, color: "#9ca3af", display: "block", marginBottom: 3 }}>Namn *</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="För- och efternamn" style={inputStyle} />
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#9ca3af", display: "block", marginBottom: 3 }}>Roll</label>
          <select value={role} onChange={e => setRole(e.target.value)} style={inputStyle}>
            <option value="extern">Extern</option>
            <option value="tekniker">Tekniker</option>
            <option value="avdelningsansvarig">Avd.ansvarig</option>
            <option value="admin_tekniker">Admin & Tekniker</option>
          </select>
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        <label style={{ fontSize: 11, color: "#9ca3af", display: "block", marginBottom: 3 }}>E-postadress *</label>
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="elektriker@foretag.se" type="email" style={inputStyle} />
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 11, color: "#9ca3af", display: "block", marginBottom: 3 }}>Tillfälligt lösenord * (de ändrar det via mail)</label>
        <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Minst 6 tecken" type="text" style={inputStyle} />
      </div>
      {error && <p style={{ fontSize: 12, color: "#dc2626", marginBottom: 8 }}>{error}</p>}
      <Btn onClick={invite} disabled={saving || !email || !name || !password} style={{ width: "100%" }}>
        {saving ? "Skapar konto…" : "✉️ Skapa & skicka inbjudan"}
      </Btn>
    </div>
  );
}

function ListEditor({ label, items, onChange, disabled }) {
  const [newItem, setNewItem] = useState("");
  function add() { const v = newItem.trim(); if (!v || items.includes(v)) return; onChange([...items, v]); setNewItem(""); }
  function remove(item) { onChange(items.filter(i => i !== item)); }
  return (
    <div style={{ marginBottom: 16 }}>
      <p style={{ margin: "0 0 8px", fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</p>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
        {items.map(item => (
          <div key={item} style={{ display: "flex", alignItems: "center", gap: 4, background: "#f3f4f6", borderRadius: 20, padding: "4px 10px" }}>
            <span style={{ fontSize: 13, color: "#374151" }}>{item}</span>
            {!disabled && <button onClick={() => remove(item)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>}
          </div>
        ))}
      </div>
      {!disabled && (
        <div style={{ display: "flex", gap: 8 }}>
          <input value={newItem} onChange={e => setNewItem(e.target.value)} placeholder="Lägg till…" onKeyDown={e => { if (e.key === "Enter") add(); }} style={{ ...inputStyle, flex: 1, fontSize: 13 }} />
          <button onClick={add} disabled={!newItem.trim()} style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: 10, padding: "8px 14px", fontSize: 13, fontWeight: 600, cursor: newItem.trim() ? "pointer" : "not-allowed", opacity: newItem.trim() ? 1 : 0.5, fontFamily: "inherit" }}>+ Lägg till</button>
        </div>
      )}
    </div>
  );
}

// ─── SETTINGS VIEW ────────────────────────────────────────────────────────────
function SettingsView({ currentUser, userMap, settings, onSettingsSaved, properties, categories, onListsChanged }) {
  const [companyName, setCompanyName] = useState(settings?.companyName || "Hotell Bruksvallsliden");
  const [logoUrl, setLogoUrl] = useState(settings?.logoUrl || "");
  const [editProperties, setEditProperties] = useState(properties);
  const [editCategories, setEditCategories] = useState(categories);
  const [notifSettings, setNotifSettings] = useState({ newTicket: true, assigned: true, newComment: true });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [roleChanges, setRoleChanges] = useState({});
  const fileRef = useRef();
  const isAdmin = currentUser.role === "admin";

  useEffect(() => { setEditProperties(properties); }, [properties]);
  useEffect(() => { setEditCategories(categories); }, [categories]);
  useEffect(() => {
    getDoc(doc(db, "settings", "notifications")).then(snap => { if (snap.exists()) setNotifSettings(snap.data()); });
  }, []);

  async function handleLogoUpload(e) {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try { const storageRef = ref(storage, "settings/logo"); await uploadBytes(storageRef, file); const url = await getDownloadURL(storageRef); setLogoUrl(url); }
    catch { alert("Kunde inte ladda upp logotypen."); }
    setUploading(false);
  }

  async function saveSettings() {
    setSaving(true);
    await setDoc(doc(db, "settings", "app"), { companyName, logoUrl, properties: editProperties, categories: editCategories, updatedAt: serverTimestamp() });
    await setDoc(doc(db, "settings", "notifications"), notifSettings);
    for (const [uid, role] of Object.entries(roleChanges)) { await updateDoc(doc(db, "users", uid), { role }); }
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000);
    onSettingsSaved({ companyName, logoUrl });
    onListsChanged(editProperties, editCategories);
    setRoleChanges({});
  }

  return (
    <div>
      {!isAdmin && <div style={{ background: "#fffbeb", border: "1.5px solid #fde68a", borderRadius: 12, padding: "12px 16px", marginBottom: 20 }}><p style={{ margin: 0, fontSize: 13, color: "#92400e" }}>⚠️ Endast administratörer kan ändra inställningar.</p></div>}

      <section style={{ background: "#fff", borderRadius: 16, padding: 20, marginBottom: 16, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
        <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#374151" }}>🏢 Företagsinformation</h3>
        <FieldGroup label="Företagsnamn"><input value={companyName} onChange={e => setCompanyName(e.target.value)} disabled={!isAdmin} style={{ ...inputStyle, opacity: isAdmin ? 1 : 0.6 }} /></FieldGroup>
        <FieldGroup label="Logotyp">
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {logoUrl ? <img src={logoUrl} alt="logo" style={{ height: 48, objectFit: "contain", borderRadius: 8, border: "1px solid #e5e7eb", padding: 4 }} /> : <div style={{ width: 48, height: 48, borderRadius: 8, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22 }}>🔧</div>}
            <div>
              <button onClick={() => fileRef.current?.click()} disabled={!isAdmin || uploading} style={{ background: "#f3f4f6", border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "7px 14px", fontSize: 13, cursor: isAdmin ? "pointer" : "not-allowed", fontFamily: "inherit", opacity: isAdmin ? 1 : 0.6 }}>
                {uploading ? "Laddar upp…" : "Byt logotyp"}
              </button>
              <p style={{ margin: "4px 0 0", fontSize: 11, color: "#9ca3af" }}>PNG, JPG eller SVG.</p>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: "none" }} />
            </div>
          </div>
        </FieldGroup>
      </section>

      {isAdmin && (
        <section style={{ background: "#fff", borderRadius: 16, padding: 20, marginBottom: 16, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#374151" }}>🔔 Notifikationsinställningar</h3>
          <p style={{ margin: "0 0 12px", fontSize: 12, color: "#9ca3af" }}>Välj vilka händelser som skickar pushnotis till teknikerna.</p>
          {[
            { key: "newTicket", label: "Nytt ärende skapas" },
            { key: "assigned", label: "Ärende tilldelas dem" },
            { key: "newComment", label: "Ny kommentar på deras ärende" },
            { key: "ticketDone", label: "Ärende klart (notis till den som skapade ärendet)" },
          ].map(({ key, label }) => (
            <label key={key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer" }}>
              <input type="checkbox" checked={notifSettings[key] ?? true} onChange={e => setNotifSettings(p => ({ ...p, [key]: e.target.checked }))}
                style={{ width: 16, height: 16, accentColor: "#6366f1", cursor: "pointer" }} />
              <span style={{ fontSize: 14, color: "#374151" }}>{label}</span>
            </label>
          ))}
        </section>
      )}

      {isAdmin && (
        <section style={{ background: "#fff", borderRadius: 16, padding: 20, marginBottom: 16, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#374151" }}>🏢 Fastigheter/avdelningar & kategorier</h3>
          <ListEditor label="Fastighet/avdelning" items={editProperties} onChange={setEditProperties} disabled={!isAdmin} />
          <ListEditor label="Kategori" items={editCategories} onChange={setEditCategories} disabled={!isAdmin} />
        </section>
      )}

      {isAdmin && (
        <section style={{ background: "#fff", borderRadius: 16, padding: 20, marginBottom: 16, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700, color: "#374151" }}>✉️ Bjud in extern användare</h3>
          <p style={{ margin: "0 0 12px", fontSize: 12, color: "#9ca3af" }}>Bjud in elektriker, hantverkare etc. De får ett mail och sätter ett lösenord.</p>
          <InviteUser />
        </section>
      )}
      {isAdmin && (
        <section style={{ background: "#fff", borderRadius: 16, padding: 20, marginBottom: 16, boxShadow: "0 1px 6px rgba(0,0,0,0.05)" }}>
          <h3 style={{ margin: "0 0 16px", fontSize: 14, fontWeight: 700, color: "#374151" }}>👥 Användare & roller</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {Object.values(userMap).map(u => (
              <div key={u.uid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "#f9fafb", borderRadius: 10 }}>
                <Avatar name={u.displayName} photo={u.photoURL} size={32} />
                <div style={{ flex: 1 }}><p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#111827" }}>{u.displayName}</p><p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>{u.email}</p></div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <select value={roleChanges[u.uid] ?? u.role ?? "tekniker"} onChange={e => setRoleChanges(prev => ({ ...prev, [u.uid]: e.target.value }))}
                    style={{ border: "1.5px solid #e5e7eb", borderRadius: 8, padding: "4px 6px", fontSize: 12, color: "#374151", background: "#fff", outline: "none", fontFamily: "inherit" }}>
                    <option value="tekniker">Tekniker</option>
                    <option value="avdelningsansvarig">Avd.ansvarig</option>
                    <option value="admin">Admin</option>
                    <option value="admin_tekniker">Admin & Tekniker</option>
                    <option value="extern">Extern</option>
                  </select>
                  {u.uid !== currentUser.uid && (
                    <button onClick={async () => {
                      if (!window.confirm("Ta bort " + u.displayName + "?")) return;
                      await deleteDoc(doc(db, "users", u.uid));
                    }} style={{ background: "#fef2f2", border: "none", borderRadius: 6, padding: "3px 6px", fontSize: 10, color: "#dc2626", cursor: "pointer", fontFamily: "inherit" }}>
                      Ta bort
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {isAdmin && <Btn onClick={saveSettings} disabled={saving} style={{ width: "100%" }}>{saving ? "Sparar…" : saved ? "✓ Sparat!" : "Spara inställningar"}</Btn>}
    </div>
  );
}

// ─── MAIN APP ─────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userMap, setUserMap] = useState({});
  const [tickets, setTickets] = useState([]);
  const [settings, setSettings] = useState({ companyName: "Hotell Bruksvallsliden", logoUrl: "" });
  const [properties, setProperties] = useState(DEFAULT_PROPERTIES);
  const [categories, setCategories] = useState(DEFAULT_DEPARTMENTS);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [filterProp, setFilterProp] = useState("Alla");
  const [filterStatus, setFilterStatus] = useState("Alla");
  const [filterPrio, setFilterPrio] = useState("Alla");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("alla");

  // Capture PWA install prompt
  useEffect(() => {
    function handleInstallPrompt(e) {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallBanner(true);
    }
    window.addEventListener("beforeinstallprompt", handleInstallPrompt);
    return () => window.removeEventListener("beforeinstallprompt", handleInstallPrompt);
  }, []);

  // Load settings before auth so logo shows on login screen
  useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "app"), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setSettings(data);
        if (data.properties?.length) setProperties(data.properties);
        if (data.categories?.length) setCategories(data.categories);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      if (u) {
        // Domain check for Google sign-in users
        const isGoogleUser = u.providerData?.some(p => p.providerId === "google.com");
        if (isGoogleUser && !isAllowedEmail(u.email)) {
          await signOut(auth);
          setAccessDenied(true);
          setAuthLoading(false);
          return;
        }
        setAccessDenied(false);
        setUser(u);
        const r = doc(db, "users", u.uid);
        const snap = await getDoc(r);
        if (!snap.exists()) {
          const displayName = u.displayName || u.email.split("@")[0];
          await setDoc(r, { uid: u.uid, displayName, email: u.email, photoURL: u.photoURL || "", role: "extern" });
          setUserProfile({ uid: u.uid, displayName, email: u.email, photoURL: u.photoURL || "", role: "extern" });
        } else {
          const profileData = { uid: u.uid, ...snap.data() };
        setUserProfile(profileData);
        sessionStorage.setItem("userProfile", JSON.stringify(profileData));
        }
        const profile = snap.exists() ? snap.data() : { role: "extern" };
        if (["tekniker", "avdelningsansvarig", "admin", "admin_tekniker"].includes(profile.role)) {
          setTimeout(() => requestPushPermission(u.uid), 3000);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setAuthLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(collection(db, "users"), snap => {
      const map = {};
      snap.docs.forEach(d => { map[d.id] = { uid: d.id, ...d.data() }; });
      setUserMap(map);
    });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, "tickets"), orderBy("createdAt", "desc"));
    return onSnapshot(q, snap => {
      const t = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setTickets(t);
      // Update app badge — only for admin/tekniker roles
      const profile = JSON.parse(sessionStorage.getItem("userProfile") || "{}");
      const badgeRoles = ["admin", "admin_tekniker", "tekniker", "avdelningsansvarig"];
      if (badgeRoles.includes(profile.role)) {
        const nyCount = t.filter(x => x.status === "Ny").length;
        try {
          if ("setAppBadge" in navigator) {
            nyCount > 0 ? navigator.setAppBadge(nyCount) : navigator.clearAppBadge();
          }
        } catch(e) {}
      }
    });
  }, [user]);

  // Settings loaded before auth (see above)

  // In-app notification listener
  useEffect(() => {
    if (!messaging || !user) return;
    return onMessage(messaging, payload => {
      const { title, body } = payload.notification || {};
      if (title) {
        // Show a simple in-app toast
        const toast = document.createElement("div");
        toast.style.cssText = "position:fixed;top:20px;right:20px;background:#1f2937;color:#fff;padding:12px 16px;border-radius:12px;z-index:9999;font-family:Inter,sans-serif;font-size:13px;max-width:300px;box-shadow:0 4px 20px rgba(0,0,0,0.3)";
        toast.innerHTML = `<strong>${title}</strong><br>${body || ""}`;
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 5000);
      }
    });
  }, [user]);

  async function handleLogin() {
    setLoginLoading(true);
    try { await signInWithPopup(auth, provider); } catch (e) { console.error(e); }
    setLoginLoading(false);
  }

  if (authLoading) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f5fb" }}>
      <div style={{ textAlign: "center" }}><div style={{ fontSize: 40, marginBottom: 8 }}>🔧</div><p style={{ fontSize: 14, color: "#9ca3af" }}>Laddar…</p></div>
    </div>
  );

  if (accessDenied) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f5f5fb", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 24, padding: "40px 32px", maxWidth: 380, width: "100%", textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,0.08)" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: "#111827", marginBottom: 8 }}>Åtkomst nekad</h2>
        <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 24, lineHeight: 1.6 }}>
          Ditt Google-konto tillhör inte en godkänd domän.<br/>
          Kontakta din administratör för hjälp.
        </p>
        <button onClick={() => { setAccessDenied(false); }}
          style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: 12, padding: "11px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
          Tillbaka till inloggning
        </button>
      </div>
    </div>
  );

  if (!user || !userProfile) return <LoginScreen onLogin={handleLogin} loading={loginLoading} settings={settings} />;

  const isAdmin = ["admin", "admin_tekniker"].includes(userProfile.role);
  const activeTickets = tickets.filter(t => t.status !== "Klar" && t.status !== "Avbruten");

  const filtered = activeTickets.filter(t => {
    if (tab === "mina" && t.assigneeId !== user.uid && t.reporterId !== user.uid) return false;
    if (filterProp !== "Alla" && t.property !== filterProp) return false;
    if (filterStatus !== "Alla" && t.status !== filterStatus) return false;
    if (filterPrio !== "Alla" && t.priority !== filterPrio) return false;
    if (search && !t.title?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const po = { Akut: 0, Hög: 1, Normal: 2, Låg: 3 };
    return (po[a.priority] ?? 2) - (po[b.priority] ?? 2);
  });

  const stats = {
    ny: activeTickets.filter(t => t.status === "Ny").length,
    pågående: activeTickets.filter(t => t.status === "Pågående").length,
    akut: activeTickets.filter(t => t.priority === "Akut").length,
  };

  const showList = tab === "alla" || tab === "mina";

  return (
    <div style={{ minHeight: "100vh", background: "#f5f5fb", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ background: "#fff", borderBottom: "1px solid #f0f0f8", position: "sticky", top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 700, margin: "0 auto", padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {settings.logoUrl ? <img src={settings.logoUrl} alt="logo" style={{ height: 36, objectFit: "contain" }} /> : <div style={{ width: 36, height: 36, background: "linear-gradient(135deg,#6366f1,#8b5cf6)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>🔧</div>}
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 800, color: "#111827" }}>Serviceärenden</p>
              <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>{settings.companyName || "Hotell Bruksvallsliden"}</p>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {isAdmin && (
              <button onClick={() => setTab(t => t === "installningar" ? "alla" : "installningar")}
                style={{ background: tab === "installningar" ? "#eef2ff" : "#f9fafb", border: "1px solid " + (tab === "installningar" ? "#c7d2fe" : "#e5e7eb"), borderRadius: 8, padding: "5px 10px", fontSize: 16, color: tab === "installningar" ? "#6366f1" : "#6b7280", cursor: "pointer" }}>
                ⚙️
              </button>
            )}
            <Avatar name={user.displayName} photo={user.photoURL} size={30} />
            <button onClick={() => signOut(auth)} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "5px 10px", fontSize: 12, color: "#6b7280", cursor: "pointer", fontFamily: "inherit" }}>Logga ut</button>
          </div>
        </div>
      </div>

      {/* PWA Install Banner */}
      {showInstallBanner && installPrompt && (
        <div style={{ background: "linear-gradient(135deg,#6366f1,#8b5cf6)", padding: "10px 16px", display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 20 }}>📲</span>
          <div style={{ flex: 1 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#fff" }}>Installera som app</p>
            <p style={{ margin: 0, fontSize: 11, color: "#c7d2fe" }}>Lägg till på hemskärmen för snabb åtkomst</p>
          </div>
          <button onClick={async () => {
            installPrompt.prompt();
            const { outcome } = await installPrompt.userChoice;
            setShowInstallBanner(false);
            setInstallPrompt(null);
          }} style={{ background: "#fff", color: "#6366f1", border: "none", borderRadius: 8, padding: "6px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}>
            Installera
          </button>
          <button onClick={() => setShowInstallBanner(false)} style={{ background: "none", border: "none", color: "#c7d2fe", fontSize: 20, cursor: "pointer", padding: 0, lineHeight: 1 }}>×</button>
        </div>
      )}

      <div style={{ maxWidth: 700, margin: "0 auto", padding: "16px" }}>
        {showList && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
            {[
              { label: "Nya", val: stats.ny, color: "#6366f1", bg: "#eef2ff" },
              { label: "Pågående", val: stats.pågående, color: "#0891b2", bg: "#ecfeff" },
              { label: "Akuta", val: stats.akut, color: "#dc2626", bg: "#fef2f2" },
            ].map(s => (
              <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: "10px 14px", textAlign: "center" }}>
                <p style={{ margin: 0, fontSize: 24, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.val}</p>
                <p style={{ margin: "2px 0 0", fontSize: 11, color: s.color, fontWeight: 600, opacity: 0.7 }}>{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Navigation */}
        <div style={{ display: "flex", gap: 3, marginBottom: 16, background: "#fff", borderRadius: 12, padding: 4, boxShadow: "0 1px 4px rgba(0,0,0,0.06)", overflowX: "auto" }}>
          {[
            { key: "alla", label: "Aktiva" },
            { key: "mina", label: "Mina" },
            { key: "onskelista", label: "⭐ Önskelista" },
            { key: "historik", label: "Historik" },
            { key: "vader", label: "🌡️ Väder" },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ flex: t.key === "installningar" ? 0 : 1, padding: "7px 8px", borderRadius: 9, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 600, background: tab === t.key ? "#6366f1" : "transparent", color: tab === t.key ? "#fff" : "#6b7280", transition: "all 0.15s", whiteSpace: "nowrap" }}>
              {t.label}
            </button>
          ))}
          {showList && (
            <button onClick={() => setShowNew(true)} style={{ background: "#6366f1", color: "#fff", border: "none", borderRadius: 9, padding: "7px 14px", fontSize: 18, cursor: "pointer", fontWeight: 700, flexShrink: 0 }}>+</button>
          )}
        </div>

        {showList && (
          <div style={{ display: "flex", gap: 6, marginBottom: 12, overflowX: "auto", paddingBottom: 2 }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Sök…"
              style={{ border: "1.5px solid #e5e7eb", borderRadius: 20, padding: "6px 12px", fontSize: 13, color: "#374151", background: "#fff", outline: "none", flexShrink: 0, width: 120, fontFamily: "inherit" }} />
            {[
              { val: filterProp, set: setFilterProp, opts: ["Alla", ...properties] },
              { val: filterStatus, set: setFilterStatus, opts: ["Alla", ...STATUSES.filter(s => s !== "Klar" && s !== "Avbruten")] },
              { val: filterPrio, set: setFilterPrio, opts: ["Alla", ...PRIORITIES] },
            ].map((f, i) => (
              <select key={i} value={f.val} onChange={e => f.set(e.target.value)}
                style={{ border: "1.5px solid #e5e7eb", borderRadius: 20, padding: "6px 10px", fontSize: 12, color: "#374151", background: "#fff", outline: "none", cursor: "pointer", flexShrink: 0, fontFamily: "inherit" }}>
                {f.opts.map(o => <option key={o}>{o}</option>)}
              </select>
            ))}
          </div>
        )}

        {showList && (
          sorted.length === 0
            ? <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af" }}><div style={{ fontSize: 40, marginBottom: 8 }}>✅</div><p style={{ fontSize: 14 }}>Inga aktiva ärenden.</p></div>
            : sorted.map(t => <TicketCard key={t.id} ticket={t} userMap={userMap} onClick={() => setSelected(t)} />)
        )}

        {tab === "onskelista" && <WishlistView currentUser={userProfile} />}
        {tab === "historik" && <HistoryView tickets={tickets} userMap={userMap} onSelect={setSelected} companyName={settings.companyName} properties={properties} />}
        {tab === "vader" && <WeatherView />}
        {tab === "installningar" && isAdmin && (
          <SettingsView currentUser={userProfile} userMap={userMap} settings={settings}
            onSettingsSaved={s => setSettings(p => ({ ...p, ...s }))}
            properties={properties} categories={categories}
            onListsChanged={(p, c) => { setProperties(p); setCategories(c); }} />
        )}

        {showList && <p style={{ textAlign: "center", fontSize: 11, color: "#d1d5db", marginTop: 16 }}>{sorted.length} av {activeTickets.length} aktiva ärenden</p>}
      </div>

      {showNew && <NewTicketForm currentUser={userProfile} userMap={userMap} onClose={() => setShowNew(false)} properties={properties} categories={categories} />}
      {selected && <TicketDetail ticket={selected} currentUser={userProfile} userMap={userMap} onClose={() => setSelected(null)} properties={properties} categories={categories} />}
    </div>
  );
}
